// ── Locale type ───────────────────────────────────────
export type SupportedLocale = "en" | "es" | "fr" | "de" | "pt" | "it";

export interface LocaleConfig {
  code: SupportedLocale;
  label: string;
  currency: string;
  dateFormat: Intl.DateTimeFormatOptions;
  timeFormat: { hour12: boolean };
  dir: "ltr" | "rtl";
}

export const LOCALE_CONFIGS: Record<SupportedLocale, LocaleConfig> = {
  en: { code: "en", label: "English",    currency: "USD", dateFormat: { year: "numeric", month: "short", day: "numeric" }, timeFormat: { hour12: true  }, dir: "ltr" },
  es: { code: "es", label: "Español",    currency: "EUR", dateFormat: { year: "numeric", month: "short", day: "numeric" }, timeFormat: { hour12: false }, dir: "ltr" },
  fr: { code: "fr", label: "Français",   currency: "EUR", dateFormat: { year: "numeric", month: "long",  day: "numeric" }, timeFormat: { hour12: false }, dir: "ltr" },
  de: { code: "de", label: "Deutsch",    currency: "EUR", dateFormat: { year: "numeric", month: "short", day: "numeric" }, timeFormat: { hour12: false }, dir: "ltr" },
  pt: { code: "pt", label: "Português",  currency: "BRL", dateFormat: { year: "numeric", month: "short", day: "numeric" }, timeFormat: { hour12: false }, dir: "ltr" },
  it: { code: "it", label: "Italiano",   currency: "EUR", dateFormat: { year: "numeric", month: "long",  day: "numeric" }, timeFormat: { hour12: false }, dir: "ltr" },
};

// ── Translation dictionary ────────────────────────────
export type TranslationKey = string;
export type TranslationDict = Record<TranslationKey, string>;
export type Translations = Partial<Record<SupportedLocale, TranslationDict>>;

// ── i18n engine ───────────────────────────────────────
export class I18n {
  private locale: SupportedLocale;
  private translations: Translations;
  private fallback: SupportedLocale = "en";

  constructor(locale: SupportedLocale = "en", translations: Translations = {}) {
    this.locale = locale;
    this.translations = translations;
  }

  setLocale(locale: SupportedLocale) {
    this.locale = locale;
    document.documentElement.lang = locale;
    document.documentElement.dir = LOCALE_CONFIGS[locale].dir;
  }

  getLocale(): SupportedLocale {
    return this.locale;
  }

  t(key: TranslationKey, vars?: Record<string, string | number>): string {
    const primary = this.translations[this.locale] ?? {};
    const fallback = this.translations[this.fallback] ?? {};
    let str = primary[key] ?? fallback[key] ?? key;
    if (vars) {
      Object.entries(vars).forEach(([k, v]) => {
        str = str.replace(new RegExp(`\\{${k}\\}`, "g"), String(v));
      });
    }
    return str;
  }

  formatCurrency(amount: number, currency?: string): string {
    const cfg = LOCALE_CONFIGS[this.locale];
    return new Intl.NumberFormat(this.locale, {
      style: "currency",
      currency: currency ?? cfg.currency,
      minimumFractionDigits: 2,
    }).format(amount);
  }

  formatDate(date: Date | string | number, options?: Intl.DateTimeFormatOptions): string {
    const cfg = LOCALE_CONFIGS[this.locale];
    return new Intl.DateTimeFormat(this.locale, {
      ...cfg.dateFormat,
      ...options,
    }).format(new Date(date));
  }

  formatTime(date: Date | string | number): string {
    const cfg = LOCALE_CONFIGS[this.locale];
    return new Intl.DateTimeFormat(this.locale, {
      hour: "numeric",
      minute: "2-digit",
      ...cfg.timeFormat,
    }).format(new Date(date));
  }

  formatRelative(date: Date): string {
    const diff = Date.now() - new Date(date).getTime();
    const rtf = new Intl.RelativeTimeFormat(this.locale, { numeric: "auto" });
    const seconds = Math.round(diff / 1000);
    const minutes = Math.round(seconds / 60);
    const hours = Math.round(minutes / 60);
    const days = Math.round(hours / 24);
    if (Math.abs(seconds) < 60) return rtf.format(-seconds, "second");
    if (Math.abs(minutes) < 60) return rtf.format(-minutes, "minute");
    if (Math.abs(hours) < 24) return rtf.format(-hours, "hour");
    return rtf.format(-days, "day");
  }
}
